import React from "react";
import { Helmet } from "react-helmet-async";
import { resolveMediaUrl } from "@/lib/api";

const DEFAULT_SITE_URL = "https://projectrs.pl";
const DEFAULT_TITLE =
    "Project Ruda Śląska - Mapa 1:1 do Euro Truck Simulator 2";
const DEFAULT_DESCRIPTION =
    "Project Ruda Śląska - fanowska modyfikacja mapy 1:1 do Euro Truck Simulator 2 (ETS2), przedstawiająca tereny miasta Rudy Śląskiej. Pobierz mapę, zobacz interaktywną mapę miasta i archiwum wersji.";
const DEFAULT_KEYWORDS =
    "Project Ruda Śląska, ETS2, Euro Truck Simulator 2, mapa, mod, Ruda Śląska, mapa 1:1, modyfikacja, Śląsk, projectrs";

function trim(s) {
    return typeof s === "string" ? s.trim() : "";
}

function toAbsolute(url, base) {
    if (!url) return "";
    const u = trim(url);
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    // resolveMediaUrl handles /api/media paths against backend
    const resolved = resolveMediaUrl(u);
    if (resolved && /^https?:\/\//i.test(resolved)) return resolved;
    return `${base.replace(/\/$/, "")}${u.startsWith("/") ? "" : "/"}${u}`;
}

/**
 * <SEO /> renders per-page meta tags (title, description, canonical, OG,
 * Twitter) plus optional JSON-LD structured data. Site-wide defaults come
 * from /api/mod-info via the `modInfo` prop; the component falls back to
 * hard-coded defaults when modInfo is unavailable.
 */
export default function SEO({
    title,
    description,
    keywords,
    pathname = "/",
    image,
    type = "website",
    noindex = false,
    modInfo = null,
    jsonLd = null,
}) {
    const siteUrl = trim(modInfo?.seo_site_url) || DEFAULT_SITE_URL;
    const siteTitle = trim(modInfo?.seo_site_title) || DEFAULT_TITLE;
    const metaDescription =
        trim(description) ||
        trim(modInfo?.seo_meta_description) ||
        DEFAULT_DESCRIPTION;
    const metaKeywords =
        trim(keywords) || trim(modInfo?.seo_keywords) || DEFAULT_KEYWORDS;

    const brand = siteTitle.split("-")[0].trim() || "Project Ruda Śląska";
    // Browser tab title is ALWAYS exactly the brand name on every page (per
    // request) - hardcoded so it never inherits a long admin-configured
    // seo_site_title regardless of its separator (-, –, —, |, ·).
    const tabTitle = "Project Ruda Śląska";
    // OG/Twitter titles stay per-page (nicer link previews when sharing subpages).
    const finalTitle = trim(title) ? `${title} · ${brand}` : brand;

    const canonical = `${siteUrl.replace(/\/$/, "")}${pathname || "/"}`;

    const ogImage =
        toAbsolute(image, siteUrl) ||
        toAbsolute(modInfo?.og_image_url, siteUrl) ||
        toAbsolute(modInfo?.hero_image_url, siteUrl) ||
        toAbsolute(modInfo?.logo_url, siteUrl) ||
        `${siteUrl.replace(/\/$/, "")}/og-default.jpg`;

    // NOTE: dynamic <link rel="icon"> from admin-uploaded logo is handled
    // imperatively in Header.jsx (it removes any conflicting <link rel="icon">
    // tags and re-adds a single one). We intentionally do NOT set the favicon
    // via Helmet here to avoid duplicate/competing icon links.

    return (
        <Helmet>
            <html lang="pl" />
            <title>{tabTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={metaKeywords} />
            <link rel="canonical" href={canonical} />
            <meta
                name="robots"
                content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
            />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:locale" content="pl_PL" />
            <meta property="og:site_name" content="Project Ruda Śląska" />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />

            {/* RSS feed discovery + sitemap link - feed readers and crawlers
                use these to auto-detect syndication and site structure. */}
            <link
                rel="alternate"
                type="application/rss+xml"
                title="Project Ruda Śląska - Ogłoszenia"
                href={`${siteUrl.replace(/\/$/, "")}/api/rss.xml`}
            />
            <link
                rel="sitemap"
                type="application/xml"
                href={`${siteUrl.replace(/\/$/, "")}/api/sitemap.xml`}
            />

            {/* Preconnect to Google Fonts CDN - cuts ~200 ms off first render */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

            {Array.isArray(jsonLd)
                ? jsonLd.map((obj, i) => (
                      <script key={i} type="application/ld+json">
                          {JSON.stringify(obj)}
                      </script>
                  ))
                : jsonLd && (
                      <script type="application/ld+json">
                          {JSON.stringify(jsonLd)}
                      </script>
                  )}
        </Helmet>
    );
}

/**
 * Build JSON-LD payloads (Organization + WebSite + VideoGame) for the homepage
 * based on current mod_settings + socials.
 */
export function buildHomeJsonLd({ modInfo, socials = [] }) {
    const siteUrl = (
        trim(modInfo?.seo_site_url) || DEFAULT_SITE_URL
    ).replace(/\/$/, "");
    const logoAbs =
        toAbsolute(modInfo?.logo_url, siteUrl) ||
        toAbsolute(modInfo?.og_image_url, siteUrl) ||
        `${siteUrl}/og-default.jpg`;
    const imageAbs =
        toAbsolute(modInfo?.og_image_url, siteUrl) ||
        toAbsolute(modInfo?.hero_image_url, siteUrl) ||
        logoAbs;
    const description =
        trim(modInfo?.seo_meta_description) || DEFAULT_DESCRIPTION;

    const sameAs = (socials || [])
        .map((s) => trim(s?.url))
        .filter((u) => /^https?:\/\//i.test(u));

    const organization = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Project Ruda Śląska",
        url: `${siteUrl}/`,
        logo: logoAbs,
        ...(sameAs.length ? { sameAs } : {}),
    };

    const website = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Project Ruda Śląska",
        url: `${siteUrl}/`,
        inLanguage: "pl-PL",
        // Enable "Sitelinks Searchbox" - Google may render an inline search
        // box under your listing when the site has enough authority.
        potentialAction: {
            "@type": "SearchAction",
            target: `${siteUrl}/?q={search_term_string}`,
            "query-input": "required name=search_term_string",
        },
    };

    const videoGame = {
        "@context": "https://schema.org",
        "@type": "VideoGame",
        name: "Project Ruda Śląska",
        alternateName: "Project RS",
        description,
        url: `${siteUrl}/`,
        image: imageAbs,
        inLanguage: "pl-PL",
        applicationCategory: "Game",
        operatingSystem: "Windows",
        gamePlatform: "PC",
        softwareVersion: trim(modInfo?.version) || undefined,
        fileSize: trim(modInfo?.file_size) || undefined,
        requirements: trim(modInfo?.requirements) || undefined,
        author: { "@type": "Organization", name: "Project Ruda Śląska" },
        publisher: { "@type": "Organization", name: "Project Ruda Śląska" },
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "PLN",
            availability: "https://schema.org/InStock",
            url: trim(modInfo?.download_url) || `${siteUrl}/#pobierz`,
        },
        isBasedOn: {
            "@type": "VideoGame",
            name: "Euro Truck Simulator 2",
            publisher: "SCS Software",
        },
    };
    // strip undefined keys (JSON-LD prefers omission)
    Object.keys(videoGame).forEach(
        (k) => videoGame[k] === undefined && delete videoGame[k]
    );

    return [organization, website, videoGame];
}

/**
 * Build a JSON-LD FAQPage payload from a list of FAQ items so that Google
 * may show rich FAQ snippets in search results.
 */
export function buildFaqJsonLd(items = []) {
    const mainEntity = (items || [])
        .filter((it) => trim(it?.question) && trim(it?.answer))
        .map((it) => ({
            "@type": "Question",
            name: trim(it.question),
            acceptedAnswer: {
                "@type": "Answer",
                text: trim(it.answer),
            },
        }));
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity,
    };
}

/**
 * Build a JSON-LD BreadcrumbList payload - helps Google render a
 * bread-crumb trail under the search result title.
 *   items: [{ name: "Home", url: "https://…/" }, { name: "POI", url: "…/poi/x" }]
 */
export function buildBreadcrumbJsonLd(items = []) {
    const itemListElement = (items || [])
        .filter((it) => trim(it?.name) && trim(it?.url))
        .map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: trim(it.name),
            item: trim(it.url),
        }));
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement,
    };
}

/**
 * Build a JSON-LD ImageGallery payload from the public gallery + community
 * approved screenshots. Helps Google index images and pick better thumbnails.
 */
export function buildImageGalleryJsonLd({ siteUrl, images = [], name = "Galeria" }) {
    const base = (trim(siteUrl) || DEFAULT_SITE_URL).replace(/\/$/, "");
    const associatedMedia = (images || [])
        .filter((img) => trim(img?.url) || trim(img?.image_url))
        .slice(0, 30)
        .map((img) => ({
            "@type": "ImageObject",
            contentUrl: toAbsolute(img.url || img.image_url, base),
            caption: trim(img.title || img.description || img.author_name || ""),
        }));
    return {
        "@context": "https://schema.org",
        "@type": "ImageGallery",
        name,
        url: `${base}/#galeria`,
        associatedMedia,
    };
}

/**
 * Build an ItemList JSON-LD for the team roster.
 */
export function buildTeamJsonLd({ siteUrl, members = [] }) {
    const base = (trim(siteUrl) || DEFAULT_SITE_URL).replace(/\/$/, "");
    const itemListElement = (members || [])
        .filter((m) => trim(m?.name))
        .map((m, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@type": "Person",
                name: trim(m.name),
                jobTitle: trim(m.role || ""),
                image: toAbsolute(m.avatar_url, base) || undefined,
            },
        }));
    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Zespół",
        url: `${base}/#zespol`,
        itemListElement,
    };
}
